import React, { ReactElement, ComponentType, Component, ReactNode } from "react";
import Tooltip, { ITooltipProps } from "./Tooltip";
import { observable } from "mobx";
import { observer } from "mobx-react";

const ErrorTooltipTimeout = 3500;

/**
 * A tooltip HOC with an opinionated timeout for use when showing errors.
 * It will automatically hide the tooltip after the timeout, and forwards on
 * any Tooltip props.
 */
const withErrorTimeout = <P extends ITooltipProps>(WrappedTooltip: ComponentType<P>): ComponentType<P> => {
  @observer
  class WithErrorTimeout extends Component<P> {
    @observable.ref
    private showTooltip = false;

    private tooltipTimeout: any = null;

    public componentDidMount(): void {
      this.showTooltip = true;
      this.tooltipTimeout = setTimeout(() => {
        this.showTooltip = false;
      }, ErrorTooltipTimeout);
    }

    public componentWillUnmount(): void {
      if (this.tooltipTimeout) {
        clearTimeout(this.tooltipTimeout);
      }
    }

    public render(): ReactElement {
      const { props } = this;
      return <WrappedTooltip isError show={this.showTooltip} {...props} />;
    }
  }

  return WithErrorTimeout;
};

const TooltipWithErrorTimeout = withErrorTimeout(Tooltip);

type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

interface IErrorTooltipProps extends Omit<ITooltipProps, "show"> {
  renderTooltip: boolean;
  children: ReactNode;
}

/**
 * A component that extends from the existing tooltip;
 * it handles unmounting and re-mounting the tooltip componentry
 * so that tooltips can be re-triggered and hidden after a timeout.
 */
const ErrorTooltip = (props: IErrorTooltipProps): ReactElement => {
  const { children, renderTooltip } = props;
  return renderTooltip ? <TooltipWithErrorTimeout {...props} /> : <>{children}</>;
};

export default ErrorTooltip;
export { TooltipWithErrorTimeout, withErrorTimeout, ErrorTooltipTimeout };
